import { Key, useCallback, useEffect, useState } from 'react'
import moment from 'moment'
import { PeriodForSale, UnitCode } from 'lib/graphql/storeadmin'
import {
  Button,
  Form,
  FormInstance,
  Icon,
  Info,
  Input,
  Select,
  Table,
  TextArea,
  Title,
} from 'components/common'
import { ProductFormValues } from 'pages/admin/products/new'
import config from 'config'
import { PeriodForSaleModal } from '../modals'

export interface Variant {
  key?: string | Key
  selectedOptions?: string | string[]
  price?: number
  specialPrice: number
  unit?: UnitCode.PerDay | UnitCode.PerHour
  position?: number
  inventory?: number
  availableForSale?: boolean
  periodForSale?: PeriodForSale
  percentageOfProduct?: number
  relatedVariations?: string[]
  productId?: string
  productName?: string
  name?: string
}

export interface OptionAndVariantProps {
  form: FormInstance
  onVariantDelete?: (list: Variant[], index: number) => void
  productsVariantsData?: Variant[]
  dependence?: ProductFormValues
  list: Variant[]
  remove: (index: number) => void
  getKey: (index: number) => number
  resetList: (newList: Variant[]) => void
}

interface PeriodOfSaleDataType {
  periodData: PeriodForSale
  index: number
}

export interface VariationsDataType {
  relatedVariationsData: string[]
  variantId: string
  name: string
  index: number
}

export const ProductVariants = ({
  form,
  onVariantDelete,
  productsVariantsData,
  dependence,
  list,
  remove,
  getKey,
  resetList,
  ...restField
}: OptionAndVariantProps): JSX.Element => {
  const [initialLoading, setInitialLoading] = useState(false)
  const [variationDates, setVariationDates] =
    useState<[moment.Moment, moment.Moment]>(null)
  const [periodOfSaleData, setPeriodOfSalesData] =
    useState<PeriodOfSaleDataType>()
  const [showPeriodOfSalesInVariation, setPeriodOfSalesInVariation] =
    useState(false)
  const [showEditTitleInModal, setEditTitleInModal] = useState(false)

  const openPeriodForSaleModal = (data: PeriodForSale, index) => {
    const checkData = typeof data !== 'undefined' && data !== null
    setPeriodOfSalesData({
      periodData: { ...data, id: index },
      index: index,
    })
    setVariationDates(
      checkData ? [moment(data?.start), moment(data?.end)] : undefined
    )
    setPeriodOfSalesInVariation(true)
    setEditTitleInModal(checkData)
  }

  const onOk = useCallback(() => {
    const newItem = productsVariantsData.find(
      (_, index) => index === periodOfSaleData.index
    )
    const item = {
      ...newItem,
      periodForSale: variationDates
        ? {
            id: `${periodOfSaleData.index}`,
            start: variationDates?.[0] || undefined,
            end: variationDates?.[1] || undefined,
          }
        : null,
    }
    productsVariantsData.splice(periodOfSaleData.index, 1, item)
    form.setFieldsValue({ productsVariants: [...productsVariantsData] })
    resetList([...productsVariantsData])
    setPeriodOfSalesInVariation(false)
    setEditTitleInModal(true)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [variationDates])

  const handleClose = () => setPeriodOfSalesInVariation(false)

  useEffect(() => {
    resetList([...productsVariantsData])
    form.setFieldsValue({ productsVariants: [...productsVariantsData] })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dependence, form])

  const resetPeriodForSale = () => {
    try {
      setInitialLoading(true)
      const newItem = productsVariantsData.find(
        (_, index) => index === periodOfSaleData.index
      )
      const item = {
        ...newItem,
        periodForSale: null,
      }
      productsVariantsData.splice(periodOfSaleData.index, 1, item)
      form.setFieldsValue({ productsVariants: [...productsVariantsData] })
      resetList([...productsVariantsData])
      setVariationDates(undefined)
      setPeriodOfSalesData(null)
    } catch (e) {
      console.error(e)
    } finally {
      setInitialLoading(false)
      setPeriodOfSalesInVariation(false)
    }
  }

  const columns = [
    {
      title: 'バリエーション',
      dataIndex: 'selectedOptions',
      key: 'selectedOptions',
      width: 150,
      render: (text: string, row: Variant, index: number) => (
        <Form.Item
          {...restField}
          name={['params', getKey(index), 'selectedOptions']}
          style={{ margin: '0' }}
          initialValue={text || ''}
        >
          <TextArea
            autoSize
            autoComplete="off"
            bordered={false}
            disabled
            style={{ color: '#000000' }}
          />
        </Form.Item>
      ),
    },
    {
      title: '価格',
      dataIndex: 'price',
      key: 'price',
      width: 150,
      render: (text: number, row: Variant, index: number) => (
        <Form.Item
          {...restField}
          name={['params', getKey(index), 'price']}
          style={{ margin: '0' }}
          rules={[
            { required: true, message: '価格は必須です。' },
            {
              type: 'number',
              max: config.product.maxPrice,
              message: `${config.product.maxPrice} 以下の数値を入力してください。`,
              transform: (v) => Number(v),
            },
          ]}
          initialValue={text || 0}
        >
          <Input prefix="￥" type="number" />
        </Form.Item>
      ),
    },
    {
      title: (
        <Info title="土日祝日価格が 0 の場合、通常価格が料金計算時に適用されます。">
          土日祝日価格
        </Info>
      ),
      dataIndex: 'specialPrice',
      key: 'specialPrice',
      width: 150,
      render: (text: number, row: Variant, index: number) => (
        <Form.Item
          {...restField}
          name={['params', getKey(index), 'specialPrice']}
          style={{ margin: '0' }}
          rules={[
            { required: true, message: '土日祝日価格は必須です。' },
            {
              type: 'number',
              max: config.product.maxPrice,
              message: `${config.product.maxPrice} 以下の数値を入力してください。`,
              transform: (v) => Number(v),
            },
          ]}
          initialValue={text || 0}
        >
          <Input prefix="￥" type="number" />
        </Form.Item>
      ),
    },
    {
      title: <Info title="明細に記載する単位です。">単位</Info>,
      dataIndex: 'unit',
      key: 'unit',
      width: 100,
      render: (data, row: Variant, index: number) => (
        <Form.Item
          {...restField}
          name={['params', getKey(index), 'unit']}
          style={{ margin: '0' }}
          initialValue={data || UnitCode.PerDay}
        >
          <Select>
            <Select.Option value={UnitCode.PerDay}>日</Select.Option>
            <Select.Option value={UnitCode.PerHour}>時間</Select.Option>
          </Select>
        </Form.Item>
      ),
    },
    {
      title: (
        <Info title="販売期間を設定すると、指定された期間中対象の施設を予約できなくなります。">
          販売期間
        </Info>
      ),
      dataIndex: 'periodForSale',
      key: 'periodForSale',
      width: 150,
      render: (data: PeriodForSale, row: Variant, index: number) => (
        <>
          <Form.Item
            {...restField}
            name={['params', getKey(index), 'periodForSale']}
            style={{ margin: '0' }}
            initialValue={data}
          >
            <Button
              type="link"
              onClick={() => openPeriodForSaleModal(data, index)}
              style={{ margin: '0', padding: '0' }}
            >
              {data?.start !== null && typeof data?.start !== 'undefined'
                ? `${moment(data?.start).format('M月D日')} -
                ${moment(data?.end).format('M月D日')}`
                : `販売期間を追加`}
            </Button>
          </Form.Item>
        </>
      ),
    },
    {
      title: '販売状況',
      dataIndex: 'availableForSale',
      key: 'availableForSale',
      width: 120,
      render: (text, row: Variant, index: number) => (
        <Form.Item
          {...restField}
          name={['params', getKey(index), 'availableForSale']}
          style={{ margin: '0' }}
          initialValue={text}
        >
          <Select>
            <Select.Option value={true}>販売中</Select.Option>
            <Select.Option value={false}>販売停止</Select.Option>
          </Select>
        </Form.Item>
      ),
    },
    {
      dataIndex: 'operation',
      width: 50,
      fixed: 'right',
      render: (_, record, index) => (
        <Button
          danger
          icon={<Icon.DeleteFilled />}
          onClick={() => {
            onVariantDelete(list, index)
            remove(index)
          }}
        />
      ),
    },
  ]

  return (
    <>
      <Form.Item
        name="productsVariants"
        fieldKey="productsVariants"
        style={{ margin: '0' }}
      >
        <Table
          scroll={{ x: 700 }}
          title={() => <Title level={5}>バリエーション</Title>}
          dataSource={list}
          // When using fixed: 'right' or 'left', the column give type error
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          columns={columns}
          pagination={false}
        />
      </Form.Item>
      <PeriodForSaleModal
        visible={showPeriodOfSalesInVariation}
        dates={variationDates}
        initialLoading={initialLoading}
        onCancel={handleClose}
        onDateChange={setVariationDates}
        onOk={onOk}
        resetPeriodForSale={resetPeriodForSale}
        showEditTitle={showEditTitleInModal}
      />
    </>
  )
}
